import { Accordion, Badge, Card, Col, Container, Placeholder, Row } from "react-bootstrap";

import './PhysiologyDashboard.scss';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBolt } from "@fortawesome/free-solid-svg-icons";
import { LLMDashboardFeedbackBar } from "../common/LLMDashboardFeedbackBar";
import { LLMVisitFeedback } from "../../../../amplify/api-gateway/query";
import { PatientVisit } from "../../../../amplify/api-gateway/search-patients";
import { LLMDashboardMarkdownRenderer } from "../common/LLMDashboardMarkdownRenderer";
import { ScanMulti } from "../common/models";


export const PhysiologyDashboardAccordian = (props: {
    title: string,
    systemData: any,
    meldScore?: any,
    activeKey: any,
    setActiveKey: any,
    isLoading: any,
    scanMultiForCitations?: ScanMulti,
    patientVisit?: PatientVisit,
    feedbacks: LLMVisitFeedback[],
    setFeedbacks: any,
}) => {

    const getColorClass = (riskLevel: any) => {
        if (riskLevel === "Very High") {
            return "physiology-very-high";
        } else if (riskLevel === "High") {
            return "physiology-high";
        } else if (riskLevel === "Moderate") {
            return "physiology-moderate";
        } else if (riskLevel === "Low") {
            return "physiology-low";
        } else if (riskLevel === "Unable to assess") {
            return "physiology-unable";
        } else {
            return "";
        }
    };

    const getRiskBadgeVariant = (riskLevel: any) => {
        const variants: { [key: string]: string } = {
            "Very High": "danger",
            "High": "warning",
            "Moderate": "info",
            "Low": "success",
            "Unable to assess": "secondary"
        };
        return variants[riskLevel] || "secondary";
    };

    return (
        <Accordion activeKey={props.activeKey} onSelect={(eventKey: any) => props.setActiveKey(eventKey)} className="physiology-accordian">
            <Accordion.Item eventKey={props.title} className="physiology-accordion-item">
                <Accordion.Header className={`physiology-header ${props.systemData && getColorClass(props.systemData.risk_level)}`}>
                    {
                        props.isLoading ? (
                            <Container>
                                <Row>
                                    <Col>
                                        <Placeholder as="div" animation="wave" className="modern-placeholder">
                                            <Placeholder xs={8} className="mb-2" style={{ height: '20px', borderRadius: '6px' }} />
                                            <Placeholder xs={12} style={{ height: '16px', borderRadius: '4px' }} />
                                        </Placeholder>
                                    </Col>
                                </Row>
                            </Container>
                        ) : !props.systemData ? (
                            <Container className="empty-state">
                                <Row className="align-items-center justify-content-center">
                                    <Col xs="auto">
                                        <div className="empty-state-icon">
                                            <FontAwesomeIcon icon={faBolt} />
                                        </div>
                                    </Col>
                                    <Col xs="auto">
                                        <span className="empty-state-text">No Data Available</span>
                                    </Col>
                                </Row>
                            </Container>
                        ) : (
                            <Container>
                                <Row className="align-items-center">
                                    <Col>
                                        <div className="system-title">{props.title}</div>
                                    </Col>
                                    <Col xs="auto">
                                        <Badge bg={getRiskBadgeVariant(props.systemData.risk_level)} className="risk-badge">
                                            {props.systemData.risk_level}
                                        </Badge>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col className="physiology-preview">
                                        <LLMDashboardMarkdownRenderer content={props.systemData.risk_level_justification} scanMulti={props.scanMultiForCitations} />
                                    </Col>
                                </Row>
                            </Container>
                        )
                    }
                </Accordion.Header>
                <Accordion.Body className="physiology-body">
                    {
                        !props.systemData ? (
                            <Container>
                                <Row>
                                    <Col>
                                        <Placeholder as="div" animation="wave">
                                            <Placeholder xs={12} className="mb-2" style={{ height: '16px', borderRadius: '4px' }} />
                                            <Placeholder xs={10} style={{ height: '16px', borderRadius: '4px' }} />
                                        </Placeholder>
                                    </Col>
                                </Row>
                            </Container>
                        ) : (
                            <>
                                <Row className="info-section">
                                    <Col md={12}>
                                        <div className="info-item">
                                            <span className="info-label">Structure</span>
                                            <div className="info-content">
                                                <LLMDashboardMarkdownRenderer content={props.systemData.structure} scanMulti={props.scanMultiForCitations} />
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <span className="info-label">Function</span>
                                            <div className="info-content">
                                                <LLMDashboardMarkdownRenderer content={props.systemData.function} scanMulti={props.scanMultiForCitations} />
                                            </div>
                                        </div>
                                    </Col>
                                </Row>
                                <Row>
                                    <Col md={12} className="text-right mt-3">
                                        <LLMDashboardFeedbackBar patientVisit={props.patientVisit} feedbackTarget={`physiology-${props.title}`} feedbacks={props.feedbacks} setFeedbacks={props.setFeedbacks} />
                                    </Col>
                                </Row>
                            </>
                        )
                    }
                    {
                        props.meldScore && (
                            <Card className="meld-card">
                                <Card.Body>
                                    <Row className="mb-3">
                                        <Col>
                                            <div className="meld-score-header">
                                                <span className="meld-title">MELD 3.0 Score</span>
                                                <span className="meld-score-value">{props.meldScore.meld_score}</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    <Row className="mb-3">
                                        <Col>
                                            <div className="survival-estimate">
                                                <span className="survival-label">Estimated 90-day Survival (ESLD)</span>
                                                <span className="survival-value">{props.meldScore.survival_estimate}%</span>
                                            </div>
                                        </Col>
                                    </Row>
                                    {
                                        props.meldScore.components && (
                                            <Row className="components-grid">
                                                <Col md={6}>
                                                    <div className="component-item">
                                                        <span className="component-label">Sodium</span>
                                                        <span className="component-value">{props.meldScore.components.sodium}</span>
                                                    </div>
                                                    <div className="component-item">
                                                        <span className="component-label">Creatinine</span>
                                                        <span className="component-value">{props.meldScore.components.creatinine}</span>
                                                    </div>
                                                    <div className="component-item">
                                                        <span className="component-label">Age</span>
                                                        <span className="component-value">{props.meldScore.components.age}</span>
                                                    </div>
                                                </Col>
                                                <Col md={6}>
                                                    <div className="component-item">
                                                        <span className="component-label">Bilirubin</span>
                                                        <span className="component-value">{props.meldScore.components.bilirubin}</span>
                                                    </div>
                                                    <div className="component-item">
                                                        <span className="component-label">INR</span>
                                                        <span className="component-value">{props.meldScore.components.inr}</span>
                                                    </div>
                                                </Col>
                                            </Row>
                                        )
                                    }
                                </Card.Body>
                            </Card>
                        )
                    }
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};
